import { Button, CustomTooltip } from '@fountain/fountain-ui-components';
import { Button as MuiButton, Grid, Typography } from '@material-ui/core';
import {
  MessageTemplate,
  ReasonSetting,
  ReminderSetting,
  TriggerSetting,
} from 'api-clients/monolith';
import { Reason } from 'api-clients/monolith/models/Reason';
import cx from 'classnames';
import React, { FC, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { REACT_APP_MONOLITH_BASE_URL } from 'runtimeEnvVars';

import { makeAvailableSlotsNewAvailabilityMessage } from 'containers/Auth_old/selectors';
import { StageContext } from 'containers/WorkflowEditor/contexts/stageContext';
import InfoGrey from 'images/info-grey.svg';

import { StageSettingCard } from '../StageSettingCard';
import { StageSettingCardEmptyState } from '../StageSettingCardEmptyState';
import { MessageRow } from './components/MessageRow';
import {
  ARCHIVED,
  ASSESSMENT,
  AUTOMATED,
  AVAILABILITY,
  CANCELLATION,
  CANCELLATION_V2,
  CONFIRMATION,
  CONSENT,
  DATA_COLLECTION,
  DOCUMENT_SIGNING,
  DOCUMENT_SIGNING_ACKNOWLEDGEMENT,
  HIRED,
  I9FORM,
  JOB_SELECTOR,
  JOB_SWITCHER,
  LEARNING,
  messageTypesThatAllowAddingMoreMessages,
  MULTI_STAGE,
  REJECTION,
  REMINDER,
  SCHEDULER,
  SCHEDULER_V2,
  SCHOOLKEEP,
  TECH_CHECK,
  TRIGGER,
  VIDEO_RECORDING,
  W4_FEDERAL_FORM,
  W4_STATE_FORM,
} from './constants';
import { messages } from './messages';
import { useStyles } from './styles';
import { MessageTypes } from './types';

export type StageSettingMessageCardProps = {
  messageType: MessageTypes;
  reasons?: Reason[];
  stageExternalId: string;
};

type WithTooltipMessage = typeof AVAILABILITY | typeof CONSENT | typeof TRIGGER;

type MessageRowData =
  | ReasonSetting
  | TriggerSetting
  | ReminderSetting
  | MessageTemplate;

type CardRow = {
  messageRow: MessageRowData;
  messageType: MessageTypes;
  sendWhen?: string;
};

export const StageSettingMessageCard: FC<StageSettingMessageCardProps> =
  React.memo(({ messageType, reasons, stageExternalId }) => {
    const styles = useStyles();
    const intl = useIntl();
    const { accountSlug, funnelSlug } = useParams<{
      accountSlug: string;
      funnelSlug: string;
    }>();

    const { stage, setStage } = useContext(StageContext);
    const availableSlotsNewAvailabilityMessage = useSelector(
      makeAvailableSlotsNewAvailabilityMessage(),
    );

    const {
      reason_settings: reasonSettings,
      trigger_settings: triggerSettings,
      reminder_settings: reminderSettings,
      landing_template: landingTemplate,
      landing_acknowledgement_template: landingAcknowledgementTemplate,
      confirmation_template: confirmationTemplate,
      cancellation_template: cancellationTemplate,
      availability_template: availabilityTemplate,
    } = stage;

    // Whether this stage holds its initial message until the documents are
    // ready, straight from the gate that decides it. The serializer derives
    // `defers_landing_message` from Stage#landing_message_tracked?, the same
    // predicate Stages::LandingJob and OfferLetters::SendForSignature consult.
    //
    // Deliberately not derived here from the stage's own offer-letter keys.
    // Both of those are withheld without the `offer-letter-templates` feature,
    // and the picker list narrows to owned templates without
    // `offer-letter-template-inheritance` — while the join the gate reads is
    // pruned by neither. A locally-derived answer therefore says "sends on
    // landing" for a stage that is still deferring, which is the sentence this
    // card exists to stop showing.
    //
    // Only DocumentSigningStage carries the key; the legacy DocumentSignatureStage
    // has no offer letter templates and never defers.
    const defersLandingMessage =
      stage.type === 'DocumentSigningStage' &&
      stage.additional_info.defers_landing_message;

    // A deferring stage also tells the applicant on landing that their
    // documents are being prepared, so its card holds two messages that send at
    // different moments. The serializer answers from the predicate the landing
    // job sends on.
    const sendsLandingAcknowledgement =
      messageType === DOCUMENT_SIGNING &&
      stage.type === 'DocumentSigningStage' &&
      stage.additional_info.sends_landing_acknowledgement;

    const messageRows: Record<MessageTypes, Array<MessageRowData>> = {
      [ARCHIVED]: reasonSettings ?? [],
      [ASSESSMENT]: landingTemplate ? [landingTemplate] : [],
      [AUTOMATED]: landingTemplate ? [landingTemplate] : [],
      [AVAILABILITY]: availabilityTemplate ? [availabilityTemplate] : [],
      [CANCELLATION]: cancellationTemplate ? [cancellationTemplate] : [],
      [CANCELLATION_V2]: cancellationTemplate ? [cancellationTemplate] : [],
      [CONFIRMATION]: confirmationTemplate ? [confirmationTemplate] : [],
      [CONSENT]: landingTemplate ? [landingTemplate] : [],
      [DATA_COLLECTION]: landingTemplate ? [landingTemplate] : [],
      [DOCUMENT_SIGNING]: landingTemplate ? [landingTemplate] : [],
      [DOCUMENT_SIGNING_ACKNOWLEDGEMENT]: landingAcknowledgementTemplate
        ? [landingAcknowledgementTemplate]
        : [],
      [HIRED]: landingTemplate ? [landingTemplate] : [],
      [I9FORM]: landingTemplate ? [landingTemplate] : [],
      [JOB_SELECTOR]: landingTemplate ? [landingTemplate] : [],
      [JOB_SWITCHER]: landingTemplate ? [landingTemplate] : [],
      [MULTI_STAGE]: landingTemplate ? [landingTemplate] : [],
      [REJECTION]: reasonSettings ?? [],
      [REMINDER]: reminderSettings ?? [],
      [SCHEDULER]: landingTemplate ? [landingTemplate] : [],
      [SCHEDULER_V2]: landingTemplate ? [landingTemplate] : [],
      [TECH_CHECK]: landingTemplate ? [landingTemplate] : [],
      [VIDEO_RECORDING]: landingTemplate ? [landingTemplate] : [],
      [TRIGGER]: triggerSettings ?? [],
      [SCHOOLKEEP]: landingTemplate ? [landingTemplate] : [],
      [LEARNING]: landingTemplate ? [landingTemplate] : [],
      [W4_FEDERAL_FORM]: landingTemplate ? [landingTemplate] : [],
      [W4_STATE_FORM]: landingTemplate ? [landingTemplate] : [],
    };

    const cardTitleByType: Record<MessageTypes, string> = {
      [ARCHIVED]: intl.formatMessage(messages.cardTitleOnHold),
      [ASSESSMENT]: intl.formatMessage(messages.cardTitleInitial),
      [AUTOMATED]: intl.formatMessage(messages.cardTitleInitial),
      [AVAILABILITY]: intl.formatMessage(messages.cardTitleAvailability),
      [CANCELLATION]: intl.formatMessage(messages.cardTitleCancellation),
      [CANCELLATION_V2]: intl.formatMessage(messages.cardTitleCancellation),
      [CONFIRMATION]: intl.formatMessage(messages.cardTitleConfirmation),
      [CONSENT]: intl.formatMessage(messages.cardTitleConsent),
      [DATA_COLLECTION]: intl.formatMessage(messages.cardTitleInitial),
      [DOCUMENT_SIGNING]: intl.formatMessage(messages.cardTitleInitial),
      [DOCUMENT_SIGNING_ACKNOWLEDGEMENT]: intl.formatMessage(
        messages.cardTitleInitial,
      ),
      [HIRED]: intl.formatMessage(messages.cardTitleHired),
      [I9FORM]: intl.formatMessage(messages.cardTitleInitial),
      [JOB_SELECTOR]: intl.formatMessage(messages.cardTitleInitial),
      [JOB_SWITCHER]: intl.formatMessage(messages.cardTitleInitial),
      [MULTI_STAGE]: intl.formatMessage(messages.cardTitleInitial),
      [REJECTION]: intl.formatMessage(messages.cardTitleRejection),
      [REMINDER]: intl.formatMessage(messages.cardTitleReminder),
      [SCHEDULER]: intl.formatMessage(messages.cardTitleInitial),
      [SCHEDULER_V2]: intl.formatMessage(messages.cardTitleInitial),
      [TECH_CHECK]: intl.formatMessage(messages.cardTitleInitial),
      [VIDEO_RECORDING]: intl.formatMessage(messages.cardTitleInitial),
      [TRIGGER]: intl.formatMessage(messages.cardTitleTrigger),
      [SCHOOLKEEP]: intl.formatMessage(messages.cardTitleInitial),
      [LEARNING]: intl.formatMessage(messages.cardTitleInitial),
      [W4_FEDERAL_FORM]: intl.formatMessage(messages.cardTitleInitial),
      [W4_STATE_FORM]: intl.formatMessage(messages.cardTitleInitial),
    };

    let documentSigningBodyText = messages.bodyTextInitial;
    if (sendsLandingAcknowledgement) {
      documentSigningBodyText =
        messages.bodyTextInitialWithLandingAcknowledgement;
    } else if (defersLandingMessage) {
      documentSigningBodyText =
        messages.bodyTextInitialWithOfferLetterTemplates;
    }

    const cardBodyTextByType: Record<MessageTypes, string> = {
      [ARCHIVED]: intl.formatMessage(messages.bodyTextOnHold),
      [ASSESSMENT]: intl.formatMessage(messages.bodyTextInitial),
      [AUTOMATED]: intl.formatMessage(messages.bodyTextInitial),
      [AVAILABILITY]: intl.formatMessage(messages.bodyTextAvailability),
      [CANCELLATION]: intl.formatMessage(messages.bodyTextCancellation),
      [CANCELLATION_V2]: intl.formatMessage(messages.bodyTextCancellation),
      [CONFIRMATION]: intl.formatMessage(messages.bodyTextConfirmation),
      [CONSENT]: intl.formatMessage(messages.bodyTextConsent),
      [DATA_COLLECTION]: intl.formatMessage(messages.bodyTextInitial),
      [DOCUMENT_SIGNING]: intl.formatMessage(documentSigningBodyText),
      [DOCUMENT_SIGNING_ACKNOWLEDGEMENT]: intl.formatMessage(
        messages.bodyTextInitial,
      ),
      [HIRED]: intl.formatMessage(messages.bodyTextInitial),
      [I9FORM]: intl.formatMessage(messages.bodyTextInitial),
      [JOB_SELECTOR]: intl.formatMessage(messages.bodyTextInitial),
      [JOB_SWITCHER]: intl.formatMessage(messages.bodyTextInitial),
      [MULTI_STAGE]: intl.formatMessage(messages.bodyTextInitial),
      [REJECTION]: intl.formatMessage(messages.bodyTextRejection),
      [REMINDER]: intl.formatMessage(messages.bodyTextReminder),
      [SCHEDULER]: intl.formatMessage(messages.bodyTextInitial),
      [SCHEDULER_V2]: intl.formatMessage(messages.bodyTextInitial),
      [TECH_CHECK]: intl.formatMessage(messages.bodyTextInitial),
      [VIDEO_RECORDING]: intl.formatMessage(messages.bodyTextInitial),
      [TRIGGER]: availableSlotsNewAvailabilityMessage
        ? intl.formatMessage(messages.bodyTextTrigger)
        : intl.formatMessage(messages.bodyTextTriggerOld),
      [SCHOOLKEEP]: intl.formatMessage(messages.bodyTextInitial),
      [LEARNING]: intl.formatMessage(messages.bodyTextInitial),
      [W4_FEDERAL_FORM]: intl.formatMessage(messages.bodyTextInitial),
      [W4_STATE_FORM]: intl.formatMessage(messages.bodyTextInitial),
    };

    const tooltipMessages: Record<WithTooltipMessage, string> = {
      [AVAILABILITY]: intl.formatMessage(messages.availabilityTooltip),
      [CONSENT]: intl.formatMessage(messages.consentTooltip),
      [TRIGGER]: intl.formatMessage(messages.followUpTooltip),
    };

    const addMessageHref = (type: MessageTypes) =>
      `${REACT_APP_MONOLITH_BASE_URL}/${accountSlug}/funnels/${funnelSlug}/stages/${stageExternalId}/stage_templates/new?type=${type}`;
    const tooltipText =
      messageType === TRIGGER ||
      messageType === CONSENT ||
      messageType === AVAILABILITY
        ? tooltipMessages[messageType]
        : '';

    // eslint-disable-next-line no-underscore-dangle
    const isPresent = (row: MessageRowData) => !row._destroy;

    // The two messages keep their places, landing first. A slot whose template
    // was deleted drops out, and the add link below offers it back.
    const messageSlots: Array<{
      messageType: MessageTypes;
      rows: MessageRowData[];
      sendWhen?: string;
    }> = sendsLandingAcknowledgement
      ? [
          {
            messageType: DOCUMENT_SIGNING_ACKNOWLEDGEMENT,
            rows: messageRows[DOCUMENT_SIGNING_ACKNOWLEDGEMENT],
            sendWhen: intl.formatMessage(
              messages.sendWhenApplicantLandsOnStage,
            ),
          },
          {
            messageType: DOCUMENT_SIGNING,
            rows: messageRows[DOCUMENT_SIGNING],
            sendWhen: intl.formatMessage(messages.sendWhenDocumentsReadyToSign),
          },
        ]
      : [{ messageType, rows: messageRows[messageType], sendWhen: undefined }];

    const cardRows: CardRow[] = messageSlots.flatMap(slot =>
      slot.rows.filter(isPresent).map(messageRow => ({
        messageRow,
        messageType: slot.messageType,
        sendWhen: slot.sendWhen,
      })),
    );

    const missingSlotType = sendsLandingAcknowledgement
      ? messageSlots.find(slot => !slot.rows.some(isPresent))?.messageType
      : undefined;

    // An empty card offers one message back: the card's own type, which on a
    // signing stage is the initial message. Once that row exists, the missing
    // acknowledgement is offered through the add link below.
    if (!cardRows.length) {
      const addMessageButtonHref = addMessageHref(messageType);
      return (
        <StageSettingCardEmptyState
          stageSettingCardProps={{
            title: cardTitleByType[messageType],
            isLoading: false,
            variant: 'default',
          }}
          buttonComponent={
            <Button
              href={addMessageButtonHref}
              type="secondary"
              size="small"
              autoWidth
              disableRipple
              aria-label={intl.formatMessage(messages.addMessage)}
            >
              <Typography variant="h4" color="primary">
                {intl.formatMessage(messages.addMessage)}
              </Typography>
            </Button>
          }
          bodyText={cardBodyTextByType[messageType]}
          tooltipText={tooltipText}
        />
      );
    }

    const addLinkMessageType = messageTypesThatAllowAddingMoreMessages[
      messageType
    ]
      ? messageType
      : missingSlotType;

    return (
      <StageSettingCard
        title={cardTitleByType[messageType]}
        isLoading={false}
        variant="default"
      >
        <Grid className={styles.messageCardContent}>
          <Grid className={styles.bodyText}>
            {cardBodyTextByType[messageType]}
            {tooltipText && (
              <CustomTooltip
                className={styles.infoTooltip}
                title={tooltipText}
                dense
              >
                <img
                  src={InfoGrey}
                  alt={intl.formatMessage(messages.infoIcon)}
                />
              </CustomTooltip>
            )}
          </Grid>
          <Grid
            className={cx(styles.messageRows, {
              [styles.messageRowsBeforeLink]: Boolean(addLinkMessageType),
            })}
          >
            {cardRows.map(cardRow => {
              return (
                <MessageRow
                  friendlyMessageType={cardTitleByType[cardRow.messageType]}
                  key={`${cardRow.messageType}-${cardRow.messageRow.id}`}
                  messageRow={cardRow.messageRow}
                  messageType={cardRow.messageType}
                  reasons={reasons}
                  sendWhen={cardRow.sendWhen}
                  setStage={setStage}
                />
              );
            })}
          </Grid>

          {addLinkMessageType && (
            <Grid>
              <MuiButton
                variant="text"
                disableRipple
                className={styles.addMessageLinkButton}
                href={addMessageHref(addLinkMessageType)}
                aria-label={intl.formatMessage(messages.addMessageTextButton)}
              >
                <Typography
                  variant="body2"
                  className={styles.addMessageLinkButtonText}
                >
                  {intl.formatMessage(messages.addMessageTextButton)}
                </Typography>
              </MuiButton>
            </Grid>
          )}
        </Grid>
      </StageSettingCard>
    );
  });
